"use client"

import * as SelectPrimitive from "@radix-ui/react-select"
import { Check, ChevronDown } from "lucide-react"

import { cn } from "@/lib/utils"

interface SelectOption {
  value: string
  label: string
}

interface SelectProps {
  value: string
  onChange: (value: string) => void
  options: SelectOption[]
  placeholder?: string
  disabled?: boolean
  className?: string
  itemClassName?: string
  style?: React.CSSProperties
  name?: string
  autocomplete?: string
  size?: "sm" | "md"
  "aria-label"?: string
}

const EMPTY_SENTINEL = "__empty__"

function toRadix(v: string) {
  return v === "" ? EMPTY_SENTINEL : v
}
function fromRadix(v: string) {
  return v === EMPTY_SENTINEL ? "" : v
}

export function Select({
  value,
  onChange,
  options,
  placeholder = "Selecione",
  disabled = false,
  className,
  itemClassName,
  style,
  name,
  autocomplete,
  size = "md",
  "aria-label": ariaLabel,
}: SelectProps) {
  const isSm = size === "sm"
  return (
    <SelectPrimitive.Root
      value={toRadix(value)}
      onValueChange={(v) => onChange(fromRadix(v))}
      disabled={disabled}
      name={name}
    >
      {name && (
        <input
          type="hidden"
          name={name}
          value={value}
          autoComplete={autocomplete}
        />
      )}
      <SelectPrimitive.Trigger
        aria-label={ariaLabel}
        className={cn(
          "flex w-full items-center justify-between border border-background-200 bg-white whitespace-nowrap",
          isSm
            ? "rounded-lg px-3 py-1.5 text-sm"
            : "text-body rounded-xl px-4 py-3",
          "hover:border-background-300 focus:border-primary-600 focus:ring-[3px] focus:ring-primary-500/50 focus:outline-none",
          "disabled:cursor-not-allowed disabled:opacity-50",
          "data-[placeholder]:text-text-400",
          className
        )}
        style={style}
      >
        <span className="min-w-0 truncate">
          <SelectPrimitive.Value placeholder={placeholder} />
        </span>
        <SelectPrimitive.Icon>
          <ChevronDown
            className={cn(
              "shrink-0 text-text-400",
              isSm ? "h-4 w-4" : "h-5 w-5"
            )}
          />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>

      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          className={cn(
            "z-50 overflow-hidden rounded-xl border border-background-100 bg-white shadow-lg",
            "animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
            "data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2",
            "min-w-[var(--radix-select-trigger-width)]"
          )}
          position="popper"
          sideOffset={4}
        >
          <SelectPrimitive.Viewport className="max-h-[240px] overflow-y-auto p-1">
            {options.map((option) => (
              <SelectPrimitive.Item
                key={option.value}
                value={toRadix(option.value)}
                className={cn(
                  "relative flex cursor-pointer items-center rounded-lg select-none",
                  isSm ? "px-3 py-1.5 text-sm" : "text-body px-4 py-3",
                  "text-text-950 hover:bg-primary-50 focus:bg-primary-50 focus:outline-none",
                  "data-[state=checked]:bg-primary-50 data-[state=checked]:text-primary-600",
                  itemClassName
                )}
              >
                <SelectPrimitive.ItemText>
                  {option.label}
                </SelectPrimitive.ItemText>
                <SelectPrimitive.ItemIndicator className="absolute right-3">
                  <Check className="h-4 w-4 text-primary-600" />
                </SelectPrimitive.ItemIndicator>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  )
}
